import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { AppState } from '../store/state';
import { AppDispatch } from '../store/store';
import bidSlice from '../store/bid/slice';
import {
  selectBidState,
  selectCanViewAuthors,
  selectCurrentRole,
  selectKeywordsLevels,
  selectKeywordState,
  selectTrackUsingKeywords,
  selectUserKeywordsInCurrentTrack,
} from '../store/selectors';
import Loading from '../components/Loading/Loading';
import { BidRow, BidDTO, BidOption, ConflictDTO } from '../store/bid/types';
import MyTableGridWrapper from '../components/MyTableGridWrapper/MyTableGridWrapper';
import styled from 'styled-components';
import { formatKeywordColumn } from '../helpers/table';
import SimpleProgressBar from '../components/ui/display/SimpleProgressBar/SimpleProgressBar';
import MyGridCore from '../components/MyGridCore/MyGridCore';
import { GridCellProps, GridColumnProps, GridFilterCellProps } from '@progress/kendo-react-grid';
import KeywordsCell from '../components/KeywordsCell/KeywordsCell';
import BidCell from '../components/BidCell/BidCell';
import { MultiSelectFilterCell } from '../components/MultiSelectFilterCell/MultiSelectFilterCell';
import EditConflictCell from '../components/EditConflictCell/EditConflictCell';
import { DropdownFilterCell } from '../components/DropdownFilterCell/DropdownFilterCell';
import { DEFAULT_FONT_SIZE } from '../components/MyGridRedux/MyGridRedux';
import { useTranslation } from 'react-i18next';
import AbstractCell from '../components/AbstractCell/AbstractCell';
import { getInitialDataState } from '../store/table/slice';
import { getFullName, stringifyOrganization } from '../helpers/users';
import MUIButton from '@material-ui/core/Button';
import Download2 from '../icons/Download2';
import { ExportDTO } from '../store/table/types';
import { Role } from '../store/conference/types';
import { doesHandleReviewers } from '../helpers/role';
import { Link, SearchText } from '../components/ui';
import history from '../store/history';
import { fillRoutePath } from '../helpers/path';
import { getRouteByName } from '../router/routes';
import ProgressIcon from '../icons/Progress';
import RedPointIcon from '../icons/RedPoint';
import ActionsIcon from '../icons/Actions';
import SimpleDialog from '../components/dialogs/SimpleDialog/SimpleDialog';
import SearchIcon from '../icons/Search';
import EditBidOptionDialog from '../components/dialogs/EditBidOptionDialog/EditBidOptionDialog';
import CloseIcon from '../icons/Close';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDeleteLeft } from '@fortawesome/free-solid-svg-icons';
import SubmissionBiddingCard from '../components/SubmissionBiddingCard/SubmissionBiddingCard';
import { useIsMobile } from '../helpers/window';
import { normalizeAffinity } from '../helpers/submission';

interface Props {
  loading: boolean;
  bidsById: { [key: number]: BidRow };
  bidOptionsById: { [key: number]: BidOption };
  keywordById: { [key: number]: Keyword };
  canViewAuthors: boolean;
  trackUsingKeywords: boolean | null;
  getOwnBidsAction: () => void;
  createBidAction: (data: BidDTO) => void;
  postConflictAction: (data: ConflictDTO) => void;
  keywordsLevels: KeywordsLevels;
  currentRole: Role | null;
  userKeywords: number[];
}

const BiddingPage: React.FC<Props> = ({
  loading,
  bidsById,
  bidOptionsById,
  keywordById,
  canViewAuthors,
  trackUsingKeywords,
  getOwnBidsAction,
  createBidAction,
  postConflictAction,
  keywordsLevels,
  currentRole,
  userKeywords,
}) => {
  const { t, i18n } = useTranslation();
  const [exportDTO, setExportDTO] = useState<ExportDTO>({
    count: 0,
    rowsOption: 'all',
  });

  useEffect(() => {
    if (Object.keys(bidsById).length === 0) {
      getOwnBidsAction();
    }
  }, []);

  const isMobile = useIsMobile();
  const [mobileProgressDialog, setMobileProgressDialog] = React.useState<JSX.Element | undefined>(undefined);
  const [mobileActionsDialog, setMobileActionsDialog] = React.useState<JSX.Element | undefined>(undefined);

  const [affinitySortOrder, setAffinitySortOrder] = React.useState<'asc' | 'desc'>('desc');
  const [inputSearchDraft, setInputSearchDraft] = React.useState('');
  const [inputSearch, setInputSearch] = React.useState<string>('');
  const [showMobileSearchDialog, setShowMobileSearchDialog] = React.useState(false);
  const [selectedSubmission, setSelectedSubmission] = React.useState<BidRow | null>(null);

  const hasKeywords = trackUsingKeywords && Object.keys(keywordById).length > 0 && userKeywords.length > 0;

  if (loading || !currentRole) return <Loading />;

  // Calculate the progress bar value based on: the number of bids of the selected option, the minimum bids, and the maximum bids.
  const calculateProgressBarValue = (bidsCount: number, min: number | null, max: number | null): number => {
    let divisor: number;

    let value;
    if (!bidsCount) {
      // When bids selected are zero, shows a little portion of bar (3%)
      value = 0.03;
    } else if (max) {
      // If bids' max is limited
      divisor = max;
      value = bidsCount / divisor;
    } else if (min) {
      // If bids' min is limited
      // If bidsCount is greater than the lower limit, the progress bar will keep growing but will never reach the end.
      divisor = bidsCount > min ? bidsCount + 1 : min + 1;
      value = bidsCount / divisor;
    } else {
      // If bids' max and bids' min are unlimited
      // Progress bar starts at 90% and keeps growing but will never reach the end.
      const increment = bidsCount - 1;
      value = (9 + increment) / (10 + increment);
    }
    return value * 100;
  };

  const getColumnProps = (): GridColumnProps[] => {
    let gridColumnProps: GridColumnProps[];

    if (handlesReviewers) {
      gridColumnProps = [
        {
          field: 'bid_option_id',
          title: t('Bid'),
          width: '200px',
          cell: (props: GridCellProps) => (
            <BidCell {...props} bidOptionsById={bidOptionsById} createBid={createBidAction} />
          ),
          filterCell: (props: GridFilterCellProps) => (
            <DropdownFilterCell
              {...props}
              data={Object.values(bidOptionsById)
                .map((value) => ({ id: value.id.toString(), label: value.name }))
                // @ts-ignore
                .concat([{ id: null, label: t('None') }])}
              defaultItem={{ label: t('(All)') }}
            />
          ),
        },
        { field: 'full_name', title: t('Full name'), width: '200px' },
        { field: 'organization', title: t('Organization'), width: '200px' },
        {
          field: 'role',
          title: t('Role'),
          width: '200px',
          filterCell: (props: GridFilterCellProps) => <MultiSelectFilterCell {...props} data={inputData} />,
        },
      ];
    } else {
      gridColumnProps = [
        {
          field: 'conflict',
          title: t('Conflict'),
          filter: 'boolean',
          width: '150px',
          cell: (props: GridCellProps) => <EditConflictCell {...props} postConflict={postConflictAction} />,
        },
        {
          field: 'bid_option_id',
          title: t('Bid'),
          width: '200px',
          cell: (props: GridCellProps) => (
            <BidCell {...props} bidOptionsById={bidOptionsById} createBid={createBidAction} />
          ),
          filterCell: (props: GridFilterCellProps) => (
            <DropdownFilterCell
              {...props}
              data={Object.values(bidOptionsById)
                .map((value) => ({ id: value.id.toString(), label: value.name }))
                // @ts-ignore
                .concat([{ id: null, label: t('None') }])}
              defaultItem={{ label: t('(All)') }}
            />
          ),
        },
        { field: 'title', title: t('Title'), width: '200px' },
        {
          field: 'abstract',
          title: t('Abstract'),
          width: '500px',
          cell: (props: GridCellProps) => <AbstractCell {...props} />,
        },
      ];
      if (canViewAuthors) {
        gridColumnProps.push({ field: 'authors_str', title: t('Authors'), width: '200px' });
      }
    }

    if (trackUsingKeywords) {
      gridColumnProps.push({ field: 'affinity', title: t('Affinity'), width: '200px', filter: 'numeric' });
      gridColumnProps.push({
        field: 'keywords',
        title: t('Keywords'),
        width: '200px',
        cell: (props: GridCellProps) => (
          <KeywordsCell {...props} keywordById={keywordById} groupByParent={keywordsLevels == 2} />
        ),
      });
      gridColumnProps.push({
        field: 'main_area',
        title: t('Main area'),
        width: '200px',
        filterCell: (props: GridFilterCellProps) => <MultiSelectFilterCell {...props} data={inputData} />,
      });
    }

    return gridColumnProps;
  };

  const handlesReviewers = doesHandleReviewers(currentRole);

  const isBidOptionWithinRange = (bidOption: BidOption): boolean => {
    const count = counts[bidOption.id];
    const min = bidOption.min ?? Number.NEGATIVE_INFINITY;
    const max = bidOption.max ?? Number.POSITIVE_INFINITY;

    return count >= min && count <= max;
  };

  const clearSearch = () => {
    setInputSearch('');
    setInputSearchDraft('');
  };

  // Initialize bid counts
  const counts: { [key: number]: number } = {};
  Object.values(bidOptionsById).forEach((bidOption) => {
    counts[bidOption.id] = 0;
  });

  // Initialize grid data
  const inputData: any[] = [];
  Object.values(bidsById).forEach((register) => {
    const newRegister = { ...register };

    if (!handlesReviewers && canViewAuthors) {
      // @ts-ignore
      newRegister.authors_str = newRegister.authors.map((author) => getFullName(author)).join(';');
    } else if (handlesReviewers) {
      // @ts-ignore
      newRegister.organization = stringifyOrganization(newRegister);
    }

    if (trackUsingKeywords) {
      formatKeywordColumn(newRegister, keywordById);
    }

    // Normalize affinity: Replace 'N/A' affinity with 0 for correct sorting
    newRegister.affinity = normalizeAffinity(newRegister.affinity);

    inputData.push(newRegister);

    // Add count
    if (register.bid_option_id) {
      counts[register.bid_option_id] += 1;
    }
  });

  const sortedSubmissions = Object.values(inputData)
    .sort((a, b) => {
      if (affinitySortOrder === 'asc') {
        return a.affinity - b.affinity;
      } else {
        return b.affinity - a.affinity;
      }
    })
    .filter((submission) => {
      const text = inputSearch.trim().toLowerCase();
      if (!text) return true;

      const keywordMatch =
        submission.keyword_ids?.some((id: number) => {
          const keywordName = keywordById[id]?.name?.toLowerCase();
          return keywordName?.includes(text);
        }) ?? false;

      return submission.title.toLowerCase().includes(text) || keywordMatch;
    });

  const progressAlert = Object.values(bidOptionsById).some((bidOption) => !isBidOptionWithinRange(bidOption));

  const bidStatusProgress = (
    <StyledBidStatusWrapper className="mt-4 mb-8 flex mobileCtrlBidStatusWrapper">
      {Object.values(bidOptionsById).map((bidOption, index) => {
        const count = counts[bidOption.id];

        let bidsBarColor;
        let bgColor;
        if (isBidOptionWithinRange(bidOption)) {
          bidsBarColor = 'green-600';
          bgColor = ''; //#c2f0e1
        } else {
          bidsBarColor = 'red-600';
          bgColor = ''; //#f9d8d8
        }

        let expectedBids = '';
        if (bidOption.min && bidOption.max) {
          expectedBids =
            bidOption.min > 1
              ? t('You are required a min. of {{min}} bids and max. of {{max}}', {
                  min: bidOption.min,
                  max: bidOption.max,
                })
              : t('You are required a min. of {{min}} bid and max. of {{max}}', {
                  min: bidOption.min,
                  max: bidOption.max,
                });
        } else if (bidOption.min) {
          expectedBids =
            bidOption.min > 1
              ? t('You are required at least {{min}} bids', {
                  min: bidOption.min,
                })
              : t('You are required at least {{min}} bid', {
                  min: bidOption.min,
                });
        } else if (bidOption.max) {
          expectedBids =
            bidOption.max > 1
              ? t('You are required at most {{max}} bids', {
                  max: bidOption.max,
                })
              : t('You are required at most {{max}} bid', {
                  max: bidOption.max,
                });
        } else {
          expectedBids = t('Bids unlimited');
        }

        const value = calculateProgressBarValue(count, bidOption.min, bidOption.max);

        return (
          <div
            key={index}
            className="relative bidOPtion rounded-md p-2"
            style={{ width: '20rem', backgroundColor: bgColor }}
          >
            <h3 className="text-sm font-semibold">{bidOption.name}</h3>
            <p className="text-xs mb-3">{expectedBids}</p>

            <SimpleProgressBar barColor={`bg-${bidsBarColor}`} progressValue={value} />

            {/* SCALE */}
            <div className="w-full relative mt-1 font-medium">
              <div className="w-full flex justify-between text-xs">
                <div className={`${count === 0 ? 'invisible' : ''}`}>0</div>
                <div>{bidOption.max}</div>
              </div>

              <div className={`text-${bidsBarColor} absolute top-0 text-xs text-right`} style={{ width: `${value}%` }}>
                {count}
              </div>
            </div>
          </div>
        );
      })}
    </StyledBidStatusWrapper>
  );

  const mobileHeaderButtons = (
    <>
      <button
        className="cursor-default text-sm flex items-center relative"
        onClick={() => {
          setMobileActionsDialog(undefined);
          setMobileProgressDialog(
            <SimpleDialog
              cancelButtonText={t('Close')}
              open={true}
              handleClose={() => {
                setMobileProgressDialog(undefined);
              }}
            >
              {inputData.length > 0 && bidStatusProgress}
            </SimpleDialog>,
          );
        }}
      >
        <ProgressIcon />
        {progressAlert && (
          <span className="mobileCtrlRedPoint">
            <RedPointIcon />
          </span>
        )}
      </button>

      <button
        className="cursor-pointer text-sm flex items-center relative"
        onClick={() => {
          const handleClose = () => setMobileActionsDialog(undefined);
          if (mobileActionsDialog) {
            handleClose();
          } else {
            setMobileActionsDialog(
              <div className="mobileCtrlBidPageActions">
                <Link
                  onClick={() => {
                    if (inputSearch !== '') {
                      clearSearch();
                      setShowMobileSearchDialog(false);
                    } else {
                      setShowMobileSearchDialog(true);
                    }
                    handleClose();
                  }}
                  className={inputSearch !== '' ? 'mobileCtrlActionRed' : ''}
                >
                  {inputSearch !== '' ? t('Clear search') : t('Search')}
                </Link>
                {hasKeywords && (
                  <Link
                    onClick={() => {
                      setAffinitySortOrder((prev: string) => (prev === 'asc' ? 'desc' : 'asc'));
                      handleClose();
                    }}
                  >
                    {t('Sort by Affinity')}
                    <span
                      aria-label="Sortable"
                      className={`mobileCtrlArrowSort k-icon ${
                        affinitySortOrder === 'asc' ? 'k-i-sort-asc-sm' : 'k-i-sort-desc-sm'
                      }`}
                    ></span>
                  </Link>
                )}
                {/*<Link
                  onClick={() => {
                    handleClose();
                  }}
                  className="pointer-events-none opacity-40"
                >
                  {t('View archived')}
                </Link>*/}
              </div>,
            );
          }
        }}
      >
        <ActionsIcon />
        {/*<span className="mobileCtrlRedPoint">
          <RedPoint />
        </span>*/}
      </button>

      {mobileActionsDialog}
    </>
  );

  return (
    <div className="h-full relative mobileCtrlBiddingPage">
      <div className="h-full flex flex-col relative">
        <div className="flex items-center mb-4 mobileCtrlBiddingPageTitleWrapper">
          <h2 className="font-bold text-xl mr-5 mb-3 mobileCtrlBiddingPageTitle">
            {handlesReviewers ? t('Reviewers to bid') : t('Submissions to bid')}
          </h2>

          <div className="mobileCtrlBiddingPageActionsWrapper relative">
            {isMobile ? (
              mobileHeaderButtons
            ) : (
              <StyledButtonWrapper className="toolboxButtonWrapper">
                <MUIButton
                  variant="outlined"
                  aria-label="export bids"
                  className="exportButton"
                  onClick={() => {
                    setExportDTO({
                      ...exportDTO,
                      count: exportDTO.count + 1,
                    });
                  }}
                >
                  <div className="mr-1.5">
                    <Download2 />
                  </div>
                  <span className="buttonLabel">{t('Export to Excel')}</span>
                </MUIButton>
              </StyledButtonWrapper>
            )}
          </div>
        </div>

        {/* If is mobile list submission cards else show desktop table */}
        {isMobile ? (
          inputData.length > 0 &&
          sortedSubmissions.map((submission, index) => {
            const onViewSubmissionBidDetail = () => {
              history.push(
                fillRoutePath(getRouteByName('RouteDetailSubmissionReviewer').path, {
                  id: submission.bidable_id.toString(),
                }),
              );
            };

            const keywordList = submission.keyword_ids.map((id: number) => keywordById[id]?.name).join(', ');

            return (
              <SubmissionBiddingCard
                key={index}
                submission={submission}
                canViewAuthors={canViewAuthors}
                keywordList={keywordList}
                selectedBid={bidOptionsById[submission.bid_option_id]?.name}
                onViewSubmissionBidDetail={onViewSubmissionBidDetail}
                hasKeywords={!!hasKeywords}
                onEditBid={() => {
                  setSelectedSubmission(submission);
                }}
              />
            );
          })
        ) : (
          <>
            {/* Bid options status with progress bar */}
            {inputData.length > 0 && bidStatusProgress}

            <StyledWrapper className="mb-1">
              <MyTableGridWrapper>
                <MyGridCore
                  allGridData={inputData}
                  gridSettings={{
                    gridColumnProps: getColumnProps(),
                    advancedFilter: false,
                    DATA_ITEM_KEY: 'bidable_id',
                    SELECTED_FIELD: '',
                    LABELABLE_ITEM_KEY: '',
                    fontSize: DEFAULT_FONT_SIZE,
                    dataState: {},
                  }}
                  dataStateValue={{ ...getInitialDataState(), sort: [{ field: 'affinity', dir: 'desc' }] }}
                  selectedState={{}}
                  dateFormat={null}
                  selectable={false}
                  showActionsColumn={false}
                  showFilters={true}
                  exportDTO={{
                    ...exportDTO,
                    arrangeData: (data) =>
                      data.map((row) => {
                        return {
                          ...row,
                          bid_option_id: row.bid_option_id ? bidOptionsById[row.bid_option_id].name : row.bid_option_id, // Map id => name
                        };
                      }),
                  }}
                />
              </MyTableGridWrapper>
            </StyledWrapper>
          </>
        )}
      </div>

      {mobileProgressDialog}

      {selectedSubmission && (
        <EditBidOptionDialog
          submission={selectedSubmission}
          handleClose={() => setSelectedSubmission(null)}
          bidOptionsById={bidOptionsById}
          createBidAction={createBidAction}
          postConflict={postConflictAction}
        />
      )}

      {isMobile && showMobileSearchDialog && (
        <div className="mobileCtrlSearchSubmissionWrapper">
          <div className="mobileCtrlSearchSubmissionTitle">{t('Search')}</div>
          <button
            className="mobileCtrlCloseSearch"
            onClick={() => {
              setShowMobileSearchDialog(false);
            }}
          >
            {t('Close')}
          </button>

          <SearchText
            className="h-9"
            value={inputSearchDraft}
            onChange={(e) => {
              setInputSearchDraft(e.target.value);
            }}
            placeholder={t('Enter text...')}
          />

          <button
            className={inputSearch !== '' ? 'mobileCtrlResetSearchIcon' : 'mobileCtrlSearchIcon'}
            onClick={() => {
              const applySearch = () => {
                setInputSearch(inputSearchDraft);
              };

              return inputSearch !== '' ? clearSearch() : applySearch();
            }}
          >
            {inputSearch !== '' ? <FontAwesomeIcon icon={faDeleteLeft} /> : <SearchIcon />}
          </button>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: AppState) => ({
  loading: selectBidState(state).loading || selectBidState(state).bidOptions.loading,
  bidsById: selectBidState(state).byId,
  bidOptionsById: selectBidState(state).bidOptions.byId,
  keywordById: selectKeywordState(state).keywordById,
  canViewAuthors: selectCanViewAuthors(state),
  trackUsingKeywords: selectTrackUsingKeywords(state),
  keywordsLevels: selectKeywordsLevels(state),
  currentRole: selectCurrentRole(state),
  userKeywords: selectUserKeywordsInCurrentTrack(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getOwnBidsAction: () => dispatch(bidSlice.actions['GET:BIDS:OWN']()),
  createBidAction: (data: BidDTO) => dispatch(bidSlice.actions['POST:BID'](data)),
  postConflictAction: (data: ConflictDTO) => dispatch(bidSlice.actions['POST:CONFLICT'](data)),
});

const StyledWrapper = styled.div`
  flex-grow: 1;

  .k-grid {
    min-height: auto !important;
  }
`;

const StyledButtonWrapper = styled.div<{ marginLeft?: string }>`
  margin-left: ${(props) => (props.marginLeft ? props.marginLeft : '10px')};

  button {
    border-color: ${(props) => props.theme.palette.primary.main};
    color: ${(props) => props.theme.palette.primary.main};
    height: 42px;

    span {
      font-weight: normal;
      text-transform: none;
    }
  }

  button:hover {
    opacity: 0.7;
    background-color: transparent;
  }

  .MuiButton-outlined.Mui-disabled {
    border-color: ${(props) => props.theme.palette.primary.main};
  }
  .MuiButton-root.Mui-disabled {
    color: ${(props) => props.theme.palette.primary.main};
  }
  .MuiButton-outlined.Mui-disabled,
  .MuiButton-root.Mui-disabled {
    opacity: 0.26;
  }
`;

export default connect(mapStateToProps, mapDispatchToProps)(BiddingPage);

const StyledBidStatusWrapper = styled.div`
  > div {
    margin-right: 0;
  }

  .bidOPtion {
    margin-right: 2rem;
  }

  .bidOPtion:last-child {
    margin-right: 0;
  }
`;
